import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import { Users, Plus, X, ShieldCheck, AlertCircle, Filter, RefreshCw, DraftingCompass } from "lucide-react";

// AOP-9132 §3.3 — Vue unifiée de gestion des comptes internes (Admin/OPS/Architect).
// Consomme les endpoints unifiés d'AOP-9131 : admin.staff.listInternal /
// createArchitect / deactivateArchitect + staff.create / updateRole / deactivate.
// Q3 (CEO) : Architect reste modélisé par UserAppAccess(ARCHITECT), pas de staffRole.

type StaffRole = "SUPER_ADMIN" | "ADMIN_OPS" | "ADMIN_SALES" | "ADMIN_SUPPORT";

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: "Super Admin",
  ADMIN_OPS: "Admin OPS",
  ADMIN_SALES: "Commercial",
  ADMIN_SUPPORT: "Admin Support",
  // Lot A : gérés dans la page Freelances (jamais créés ici).
  FREELANCE_SDR: "SDR freelance",
  FREELANCE_SALES: "Sales freelance",
  ARCHITECTE: "Architecte",
};

const ROLE_VARIANT: Record<string, "default" | "secondary" | "outline" | "warning"> = {
  SUPER_ADMIN: "default",
  ADMIN_OPS: "secondary",
  ADMIN_SALES: "warning",
  ADMIN_SUPPORT: "outline",
  ARCHITECTE: "outline",
};

// Ligne unifiée (staff + architect) telle qu'affichée dans le tableau.
interface InternalRow {
  kind: "STAFF" | "ARCHITECT";
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  isActive: boolean;
  createdAt: string;
}

// ─── Modal : créer un compte staff interne (Admin / OPS / Support / Commercial) ──

function CreateStaffModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [staffRole, setStaffRole] = useState<StaffRole>("ADMIN_OPS");
  const [error, setError] = useState<string | null>(null);

  const create = trpc.admin.staff.create.useMutation({
    onSuccess: () => {
      toast.success("Compte staff interne créé");
      onSuccess();
      onClose();
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    create.mutate({ email, password, firstName: firstName || undefined, lastName: lastName || undefined, staffRole });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Nouveau compte staff (Admin / OPS)
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="staff-firstName">Prénom</Label>
              <Input id="staff-firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Marie" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="staff-lastName">Nom</Label>
              <Input id="staff-lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Dupont" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="staff-email">Email *</Label>
            <Input id="staff-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="marie@constrigo.fr" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="staff-password">Mot de passe *</Label>
            <Input id="staff-password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 caractères" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="staff-role">Rôle *</Label>
            <select
              id="staff-role"
              value={staffRole}
              onChange={(e) => setStaffRole(e.target.value as StaffRole)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="ADMIN_OPS">Admin OPS</option>
              <option value="ADMIN_SALES">Commercial</option>
              <option value="ADMIN_SUPPORT">Admin Support</option>
              <option value="SUPER_ADMIN">Super Admin</option>
            </select>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="flex-1" disabled={create.isPending}>
              {create.isPending ? "Création…" : "Créer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Modal : créer un compte Architect (UserAppAccess ARCHITECT) ─────────────────

function CreateArchitectModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = trpc.admin.staff.createArchitect.useMutation({
    onSuccess: () => {
      toast.success("Compte Architect créé");
      onSuccess();
      onClose();
    },
    onError: (err) => setError(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    create.mutate({
      email,
      password: password || undefined,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border rounded-lg shadow-lg w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <DraftingCompass className="h-4 w-4 text-primary" />
            Nouveau compte Architect
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Fermer">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="arch-firstName">Prénom</Label>
              <Input id="arch-firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Léa" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="arch-lastName">Nom</Label>
              <Input id="arch-lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Bernard" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="arch-email">Email *</Label>
            <Input id="arch-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="lea@architecte.fr" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="arch-password">Mot de passe</Label>
            <Input id="arch-password" type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Requis uniquement pour un nouveau compte" />
            <p className="text-xs text-muted-foreground">
              Requis pour un nouvel email. Si le compte existe déjà, l'accès Architect lui est simplement rattaché.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="flex-1" disabled={create.isPending}>
              {create.isPending ? "Création…" : "Créer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Page principale ─────────────────────────────────────────────────────────────

export default function InternalAccounts() {
  const [showCreateStaff, setShowCreateStaff] = useState(false);
  const [showCreateArchitect, setShowCreateArchitect] = useState(false);
  const [kindFilter, setKindFilter] = useState<"" | "STAFF" | "ARCHITECT">("");
  const utils = trpc.useUtils();

  const { data, isLoading } = trpc.admin.staff.listInternal.useQuery();

  const invalidate = () => utils.admin.staff.listInternal.invalidate();

  const updateRole = trpc.admin.staff.updateRole.useMutation({
    onSuccess: () => {
      toast.success("Rôle mis à jour");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deactivateStaff = trpc.admin.staff.deactivate.useMutation({
    onSuccess: (res) => {
      toast.success(res.isActive ? "Compte réactivé" : "Compte désactivé");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deactivateArchitect = trpc.admin.staff.deactivateArchitect.useMutation({
    onSuccess: () => {
      toast.success("Compte Architect désactivé");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  // Réactiver un Architect = re-créer l'accès (idempotent, réactive si révoqué —
  // aucun mot de passe requis puisque le User existe déjà). Cf staff.ts createArchitect.
  const reactivateArchitect = trpc.admin.staff.createArchitect.useMutation({
    onSuccess: () => {
      toast.success("Compte Architect réactivé");
      invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const rows: InternalRow[] = useMemo(() => {
    if (!data) return [];
    const staff: InternalRow[] = data.staff.map((s) => ({
      kind: "STAFF",
      userId: s.userId,
      email: s.email,
      firstName: s.firstName,
      lastName: s.lastName,
      role: s.role,
      isActive: s.isActive,
      createdAt: s.createdAt,
    }));
    const architects: InternalRow[] = data.architects.map((a) => ({
      kind: "ARCHITECT",
      userId: a.userId,
      email: a.email,
      firstName: a.firstName,
      lastName: a.lastName,
      role: a.role,
      isActive: a.isActive,
      createdAt: a.createdAt,
    }));
    return [...staff, ...architects].sort((x, y) => y.createdAt.localeCompare(x.createdAt));
  }, [data]);

  const filteredRows = kindFilter ? rows.filter((r) => r.kind === kindFilter) : rows;

  const total = rows.length;
  const activeCount = rows.filter((r) => r.isActive).length;
  const architectCount = rows.filter((r) => r.kind === "ARCHITECT").length;

  const anyPending =
    updateRole.isPending || deactivateStaff.isPending || deactivateArchitect.isPending || reactivateArchitect.isPending;

  return (
    <AdminLayout>
      {showCreateStaff && <CreateStaffModal onClose={() => setShowCreateStaff(false)} onSuccess={invalidate} />}
      {showCreateArchitect && <CreateArchitectModal onClose={() => setShowCreateArchitect(false)} onSuccess={invalidate} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Comptes internes</h1>
            <p className="text-muted-foreground">Vue unifiée des comptes internes Constrigo — Admin, OPS et Architect.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => invalidate()} disabled={isLoading}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Rafraîchir
            </Button>
            <Button variant="secondary" onClick={() => setShowCreateArchitect(true)}>
              <DraftingCompass className="h-4 w-4 mr-2" />
              Nouvel Architect
            </Button>
            <Button onClick={() => setShowCreateStaff(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nouveau staff
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-3 sm:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total comptes internes</CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{isLoading ? "—" : total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Actifs</CardTitle>
              <ShieldCheck className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-success">{isLoading ? "—" : activeCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Architectes</CardTitle>
              <DraftingCompass className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{isLoading ? "—" : architectCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value as "" | "STAFF" | "ARCHITECT")}
              className="h-10 pl-9 pr-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring appearance-none cursor-pointer"
            >
              <option value="">Tous les comptes</option>
              <option value="STAFF">Staff (Admin / OPS)</option>
              <option value="ARCHITECT">Architect</option>
            </select>
          </div>
          {kindFilter && (
            <button
              onClick={() => setKindFilter("")}
              className="h-10 px-3 rounded-lg border border-input bg-background text-sm text-muted-foreground hover:bg-secondary transition-colors"
            >
              Effacer
            </button>
          )}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium text-muted-foreground">Utilisateur</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">App</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Rôle</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Statut</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Créé le</th>
                    <th className="text-left p-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-4"><Skeleton className="h-4 w-40" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-20" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-24" /></td>
                        <td className="p-4"><Skeleton className="h-5 w-16" /></td>
                        <td className="p-4"><Skeleton className="h-4 w-24" /></td>
                        <td className="p-4"><Skeleton className="h-8 w-32" /></td>
                      </tr>
                    ))
                  ) : filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground">
                        {kindFilter ? "Aucun compte pour ce filtre" : "Aucun compte interne"}
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r) => (
                      <tr key={`${r.kind}-${r.userId}`} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="p-4">
                          <div className="font-medium">
                            {[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}
                          </div>
                          <div className="text-xs text-muted-foreground">{r.email}</div>
                        </td>
                        <td className="p-4">
                          <Badge variant={r.kind === "ARCHITECT" ? "outline" : "secondary"}>
                            {r.kind === "ARCHITECT" ? "Architect" : "Backoffice"}
                          </Badge>
                        </td>
                        <td className="p-4">
                          {r.kind === "STAFF" ? (
                            <select
                              value={r.role ?? ""}
                              onChange={(e) => {
                                const newRole = e.target.value as StaffRole;
                                if (newRole !== r.role) updateRole.mutate({ userId: r.userId, staffRole: newRole });
                              }}
                              disabled={updateRole.isPending}
                              className="h-7 rounded border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                            >
                              <option value="ADMIN_OPS">Admin OPS</option>
                              <option value="ADMIN_SALES">Commercial</option>
                              <option value="ADMIN_SUPPORT">Admin Support</option>
                              <option value="SUPER_ADMIN">Super Admin</option>
                            </select>
                          ) : (
                            <Badge variant={ROLE_VARIANT[r.role ?? ""] ?? "outline"}>
                              {ROLE_LABELS[r.role ?? ""] ?? r.role ?? "—"}
                            </Badge>
                          )}
                        </td>
                        <td className="p-4">
                          <Badge variant={r.isActive ? "success" : "secondary"}>
                            {r.isActive ? "Actif" : "Inactif"}
                          </Badge>
                        </td>
                        <td className="p-4 text-muted-foreground">{formatDate(r.createdAt)}</td>
                        <td className="p-4">
                          {r.kind === "STAFF" ? (
                            <Button
                              variant={r.isActive ? "outline" : "secondary"}
                              size="sm"
                              disabled={anyPending}
                              onClick={() => deactivateStaff.mutate({ userId: r.userId })}
                            >
                              {r.isActive ? "Désactiver" : "Réactiver"}
                            </Button>
                          ) : r.isActive ? (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={anyPending}
                              onClick={() => deactivateArchitect.mutate({ userId: r.userId })}
                            >
                              Désactiver
                            </Button>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              disabled={anyPending}
                              onClick={() => reactivateArchitect.mutate({ email: r.email })}
                            >
                              Réactiver
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
